import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import PasswordStrengthIndicator from '../PasswordStrengthIndicator';
import axios from '../axiosConfig';

const CambioPasswordTemporal = () => {
  const [passwordTemporal, setPasswordTemporal] = useState('');
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    temporal: false,
    nueva: false,
    confirmar: false,
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && user && !user.requiereCambioPassword) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('Mínimo 8 caracteres');
    if (!/[A-Z]/.test(password)) errors.push('Al menos 1 mayúscula');
    if (!/[a-z]/.test(password)) errors.push('Al menos 1 minúscula');
    if (!/[0-9]/.test(password)) errors.push('Al menos 1 número');
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Al menos 1 símbolo especial');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordTemporal.trim()) {
      setError('Ingresa tu contraseña temporal');
      return;
    }

    const passwordErrors = validatePassword(nuevaPassword);
    if (passwordErrors.length > 0) {
      setError(`La nueva contraseña no cumple los requisitos: ${passwordErrors.join(', ')}`);
      return;
    }

    if (nuevaPassword !== confirmarPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (passwordTemporal === nuevaPassword) {
      setError('La nueva contraseña debe ser diferente a la temporal');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/auth/cambiar-password-temporal', {
        password_temporal: passwordTemporal,
        nueva_password: nuevaPassword,
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          logout();
          navigate('/login', { replace: true });
        }, 2800);
      }
    } catch (err) {
      console.error('Error al cambiar contraseña:', err);
      if (err.response?.data?.detail) {
        setError(
          typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail)
        );
      } else {
        setError('Error al cambiar la contraseña. Intenta nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const shellClass =
    'text-slate-900 dark:text-slate-100 min-h-screen w-full flex flex-col relative overflow-hidden';
  const shellBg = { backgroundColor: '#00125b' };

  if (success) {
    return (
      <div className={shellClass} style={shellBg}>
        <div className="absolute inset-0 bg-[#00125b]/10" />
        <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-[500px] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden text-center px-8 py-12"
          >
            <div className="mb-6 flex justify-center">
              <img
                alt="CEDIA"
                className="h-24 w-auto object-contain"
                src="/images/cedia.webp"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-4xl">
                check_circle
              </span>
            </div>
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-3">
              Contraseña actualizada
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-8">
              Serás redirigido al inicio de sesión para entrar con tu nueva contraseña.
            </p>
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </motion.div>
          <footer className="mt-10 text-center">
            <p className="text-slate-400 text-xs">
              © 2026 CEDIA — Sistema Inteligente de Gestión del Conocimiento
            </p>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass} style={shellBg}>
      <div className="absolute inset-0 bg-[#00125b]/10" />

      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-[500px] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="pt-10 pb-6 px-8 flex flex-col items-center border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800">
            <div className="mb-5">
              <img
                alt="CEDIA Logo"
                className="h-28 sm:h-32 w-auto object-contain"
                src="/images/cedia.webp"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-1 text-center">
              Sistema Inteligente de Gestión del Conocimiento
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
              Cambio de contraseña obligatorio
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-800/50">
              <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-lg">
                lock_reset
              </span>
              <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                Primera vez: define una contraseña definitiva
              </span>
            </div>
          </div>

          <div className="px-8 py-8 bg-white dark:bg-slate-800">
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-3">
                <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-xl shrink-0">
                  error
                </span>
                <p className="text-sm text-red-800 dark:text-red-200 flex-1">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Contraseña temporal
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">
                    key
                  </span>
                  <input
                    type={showPasswords.temporal ? 'text' : 'password'}
                    value={passwordTemporal}
                    onChange={(e) => setPasswordTemporal(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 transition-all"
                    placeholder="La que te asignaron"
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('temporal')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-lg">
                      {showPasswords.temporal ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">
                    lock
                  </span>
                  <input
                    type={showPasswords.nueva ? 'text' : 'password'}
                    value={nuevaPassword}
                    onChange={(e) => setNuevaPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 transition-all"
                    placeholder="Mínimo 8 caracteres, mayúscula, número y símbolo"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('nueva')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-lg">
                      {showPasswords.nueva ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {nuevaPassword ? <PasswordStrengthIndicator password={nuevaPassword} /> : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">
                    lock_clock
                  </span>
                  <input
                    type={showPasswords.confirmar ? 'text' : 'password'}
                    value={confirmarPassword}
                    onChange={(e) => setConfirmarPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 transition-all"
                    placeholder="Repite la nueva contraseña"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('confirmar')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-lg">
                      {showPasswords.confirmar ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {confirmarPassword && nuevaPassword !== confirmarPassword && (
                  <p className="mt-1 text-xs text-red-500">Las contraseñas no coinciden</p>
                )}
                {confirmarPassword && nuevaPassword === confirmarPassword && (
                  <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">check</span>
                    Coinciden
                  </p>
                )}
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 border border-slate-100 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Requisitos de la nueva contraseña
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                  {[
                    { ok: nuevaPassword.length >= 8, text: 'Mínimo 8 caracteres' },
                    { ok: /[A-Z]/.test(nuevaPassword), text: 'Al menos 1 mayúscula' },
                    { ok: /[a-z]/.test(nuevaPassword), text: 'Al menos 1 minúscula' },
                    { ok: /[0-9]/.test(nuevaPassword), text: 'Al menos 1 número' },
                    {
                      ok: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(nuevaPassword),
                      text: 'Al menos 1 símbolo (!@#$…)',
                    },
                  ].map(({ ok, text }) => (
                    <li key={text} className={`flex items-center gap-2 ${ok ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                      <span className="material-symbols-outlined text-sm">
                        {ok ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-4 rounded-lg text-sm font-semibold text-white transition-all duration-300 flex items-center justify-center space-x-2 ${
                  isSubmitting
                    ? 'bg-slate-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-light shadow-md hover:shadow-lg'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Guardando…</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">lock_reset</span>
                    <span>Establecer contraseña</span>
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900 px-8 py-4 flex justify-center gap-6 text-xs text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700">
            <span className="text-center">No compartas tu contraseña con nadie</span>
          </div>
        </div>

        <footer className="mt-12 text-center space-y-2">
          <p className="text-slate-400 text-xs">
            © 2026 CEDIA - Sistema Inteligente de Gestión del Conocimiento. Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-4 text-[10px] text-slate-500 uppercase tracking-widest">
            <span>Infraestructura Académica</span>
            <span className="opacity-30">|</span>
            <span>I+D+i</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CambioPasswordTemporal;
